/**
 * Design Navigation Script
 * Handles click events for gift card design selection
 * Waits for dynamically loaded content before attaching event listeners
 */

// Configuration: Define URLs and images for each design
const designConfig = {
    'Design 1': {
        url: 'https://thehonestlabel.com/products/the-honest-label-gift-card-spring-refresh',
        image: 'https://cdn.shopify.com/s/files/1/0572/2089/0789/files/Spring-Refresh-GiftCard_GIFT_CARD_DO_NOT_DELETE.png?v=1756954849',
        alt: 'The Honest Label Gift Card (Spring Refresh)'
    },
    'Design 2': {
        url: 'https://thehonestlabel.com/products/the-honest-label-gift-card-standard',
        image: 'https://cdn.shopify.com/s/files/1/0572/2089/0789/files/HonestLabel-GiftCard_GIFT_CARD_DO_NOT_DELETE.webp?v=1756952896',
        alt: 'The Honest Label Gift Card (Standard) '
    }
};

// Create URL to design mapping for backward compatibility
const designUrls = {};
const urlToDesign = {};
Object.keys(designConfig).forEach(designLabel => {
    designUrls[designLabel] = designConfig[designLabel].url;
    urlToDesign[designConfig[designLabel].url] = designLabel;
});

// Debug logging utility
function debugLog(message, data = null) {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] Design Navigation: ${message}`);
    if (data) {
        console.log('Data:', data);
    }
}

// Detect which design should be selected based on current page URL
function detectCurrentDesign() {
    const currentUrl = window.location.href;
    debugLog('Detecting current design based on URL:', {
        currentUrl: currentUrl,
        availableUrls: Object.keys(urlToDesign)
    });
    
    // Check for exact URL match
    if (urlToDesign[currentUrl]) {
        const designLabel = urlToDesign[currentUrl];
        debugLog(`Found exact URL match: ${designLabel}`);
        return designLabel;
    }
    
    // Check for partial URL match (in case of query parameters, fragments, etc.)
    for (const [url, designLabel] of Object.entries(urlToDesign)) {
        if (currentUrl.includes(url)) {
            debugLog(`Found partial URL match: ${designLabel} (${url})`);
            return designLabel;
        }
    }
    
    debugLog('No URL match found, will use default selection');
    return null;
}


// Wait for the card gallery container to be loaded
function waitForCardGallery() {
    debugLog('Starting to wait for card gallery container...');
    
    return new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 100; // Maximum 10 seconds (100 * 100ms)
        
        const checkForElement = () => {
            attempts++;
            debugLog(`Checking for card gallery container (attempt ${attempts}/${maxAttempts})`);
            
            const container = document.querySelector('.sc-card-gallery__container');
            
            if (container) {
                debugLog('Card gallery container found!', {
                    element: container,
                    childrenCount: container.children.length,
                    className: container.className
                });
                resolve(container);
            } else if (attempts >= maxAttempts) {
                debugLog('ERROR: Card gallery container not found after maximum attempts');
                reject(new Error('Card gallery container not found'));
            } else {
                debugLog(`Card gallery container not found, retrying in 100ms...`);
                setTimeout(checkForElement, 100);
            }
        };
        
        checkForElement();
    });
}

// Wait for all design items to be loaded
function waitForDesignItems() {
    debugLog('Waiting for design items to be loaded...');
    
    return new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 50; // Maximum 5 seconds
        
        const checkForItems = () => {
            attempts++;
            debugLog(`Checking for design items (attempt ${attempts}/${maxAttempts})`);
            
            const items = document.querySelectorAll('.sc-card-gallery__item');
            debugLog(`Found ${items.length} design items`);
            
            if (items.length >= 2) {
                debugLog('All design items found!', {
                    itemsCount: items.length,
                    items: Array.from(items).map(item => ({
                        ariaLabel: item.getAttribute('aria-label'),
                        ariaChecked: item.getAttribute('aria-checked'),
                        className: item.className
                    }))
                });
                resolve(items);
            } else if (attempts >= maxAttempts) {
                debugLog('ERROR: Not enough design items found after maximum attempts');
                reject(new Error('Not enough design items found'));
            } else {
                setTimeout(checkForItems, 100);
            }
        };
        
        checkForItems();
    });
}

// Update gift card image on the page
function updateGiftCardImage(selectedDesignLabel) {
    debugLog(`Updating gift card image for: ${selectedDesignLabel}`);
    
    const giftCardContainer = document.querySelector('.sc-gift-card__container');
    if (!giftCardContainer) {
        debugLog('Gift card container not found');
        return;
    }
    
    const giftCardImage = giftCardContainer.querySelector('img');
    if (!giftCardImage) {
        debugLog('Gift card image not found');
        return;
    }
    
    const designInfo = designConfig[selectedDesignLabel];
    if (!designInfo) {
        debugLog(`No design configuration found for: ${selectedDesignLabel}`);
        return;
    }
    
    debugLog('Updating gift card image:', {
        oldSrc: giftCardImage.src,
        newSrc: designInfo.image,
        oldAlt: giftCardImage.alt,
        newAlt: designInfo.alt
    });
    
    // Update image source and alt text
    giftCardImage.src = designInfo.image;
    giftCardImage.alt = designInfo.alt;
    
    debugLog(`Successfully updated gift card image for ${selectedDesignLabel}`);
}

// Update selection state for design items
function updateSelectionState(selectedDesignLabel) {
    debugLog(`Updating selection state for: ${selectedDesignLabel}`);
    
    const designItems = document.querySelectorAll('.sc-card-gallery__item');
    let updatedCount = 0;
    
    designItems.forEach((item, index) => {
        const designLabel = item.getAttribute('aria-label');
        const isSelected = designLabel === selectedDesignLabel;
        
        debugLog(`Processing item ${index + 1} (${designLabel}):`, {
            isTarget: isSelected,
            currentClasses: item.className,
            currentAriaChecked: item.getAttribute('aria-checked')
        });
        
        if (isSelected) {
            // Set as selected
            item.className = 'sc-card-gallery__item sc-card-gallery__item--selected';
            item.setAttribute('aria-checked', 'true');
            debugLog(`Set ${designLabel} as selected`);
        } else {
            // Set as not selected
            item.className = 'sc-card-gallery__item false';
            item.setAttribute('aria-checked', 'false');
            debugLog(`Set ${designLabel} as not selected`);
        }
        
        updatedCount++;
    });
    
    debugLog(`Updated selection state for ${updatedCount} items`);
    
    // Also update the gift card image
    updateGiftCardImage(selectedDesignLabel);
}

// Add click event listeners to design items
function addClickListeners() {
    debugLog('Adding click event listeners to design items...');
    
    const designItems = document.querySelectorAll('.sc-card-gallery__item');
    debugLog(`Processing ${designItems.length} design items`);
    
    let listenersAdded = 0;
    
    designItems.forEach((item, index) => {
        // Get the aria-label to identify which design
        const designLabel = item.getAttribute('aria-label');
        debugLog(`Processing item ${index + 1}: ${designLabel}`);
        
        if (designLabel && designUrls[designLabel]) {
            debugLog(`Adding click listener for ${designLabel}`, {
                targetUrl: designUrls[designLabel],
                element: item
            });
            
            item.addEventListener('click', (event) => {
                debugLog(`Click event triggered for ${designLabel}`, {
                    event: event,
                    targetUrl: designUrls[designLabel],
                    timestamp: new Date().toISOString()
                });
                
                // Update selection state before navigation
                debugLog(`Updating selection state before navigation...`);
                updateSelectionState(designLabel);
                
                // Small delay to ensure UI updates are visible
                setTimeout(() => {
                    // Navigate to the corresponding URL
                    try {
                        window.location.href = designUrls[designLabel];
                        debugLog(`Successfully navigated to ${designUrls[designLabel]}`);
                    } catch (error) {
                        debugLog(`ERROR: Failed to navigate to ${designUrls[designLabel]}`, error);
                    }
                }, 100); // 100ms delay to show the selection change
            });
            
            // Add cursor pointer style to indicate clickable
            item.style.cursor = 'pointer';
            debugLog(`Added pointer cursor to ${designLabel}`);
            
            listenersAdded++;
        } else {
            debugLog(`WARNING: No URL configured for ${designLabel}`, {
                availableUrls: Object.keys(designUrls),
                itemAriaLabel: designLabel
            });
        }
    });
    
    debugLog(`Successfully added ${listenersAdded} click listeners`);
}

// Verify the setup
function verifySetup() {
    debugLog('Verifying setup...');
    
    const container = document.querySelector('.sc-card-gallery__container');
    const items = document.querySelectorAll('.sc-card-gallery__item');
    const giftCardContainer = document.querySelector('.sc-gift-card__container');
    const giftCardImage = giftCardContainer ? giftCardContainer.querySelector('img') : null;
    
    debugLog('Setup verification results:', {
        containerExists: !!container,
        itemsCount: items.length,
        giftCardContainerExists: !!giftCardContainer,
        giftCardImageExists: !!giftCardImage,
        configuredUrls: Object.keys(designUrls).length,
        itemsWithListeners: Array.from(items).filter(item => {
            const label = item.getAttribute('aria-label');
            return label && designUrls[label];
        }).length
    });
    
    // Test if elements are clickable and check selection state
    items.forEach((item, index) => {
        const label = item.getAttribute('aria-label');
        const hasUrl = label && designUrls[label];
        const hasPointer = item.style.cursor === 'pointer';
        const isSelected = item.classList.contains('sc-card-gallery__item--selected');
        const ariaChecked = item.getAttribute('aria-checked');
        
        debugLog(`Item ${index + 1} (${label}) verification:`, {
            hasUrl: hasUrl,
            hasPointer: hasPointer,
            isSelected: isSelected,
            ariaChecked: ariaChecked,
            className: item.className,
            isReady: hasUrl && hasPointer
        });
    });
    
    // Check gift card image
    if (giftCardImage) {
        debugLog('Gift card image verification:', {
            src: giftCardImage.src,
            alt: giftCardImage.alt,
            width: giftCardImage.width,
            height: giftCardImage.height
        });
    } else {
        debugLog('Gift card image not found');
    }
}

// Main function to initialize the functionality
async function initializeDesignNavigation() {
    debugLog('=== Starting Design Navigation Initialization ===');
    
    try {
        // Step 1: Wait for the card gallery to be loaded
        debugLog('Step 1: Waiting for card gallery container...');
        await waitForCardGallery();
        
        // Step 2: Wait for design items to be loaded
        debugLog('Step 2: Waiting for design items...');
        await waitForDesignItems();
        
        // Step 3: Detect current design based on URL and set selection
        debugLog('Step 3: Detecting and setting current design selection...');
        const currentDesign = detectCurrentDesign();
        if (currentDesign) {
            debugLog(`Setting selection to: ${currentDesign}`);
            updateSelectionState(currentDesign);
        } else {
            debugLog('No specific design detected, keeping default selection');
        }
        
        // Step 4: Add click listeners
        debugLog('Step 4: Adding click event listeners...');
        addClickListeners();
        
        // Step 5: Verify setup
        debugLog('Step 5: Verifying setup...');
        verifySetup();
        
        debugLog('=== Design Navigation Initialization Completed Successfully ===');
        
        // Return success indicator
        return {
            success: true,
            message: 'Design navigation initialized successfully',
            currentDesign: currentDesign,
            timestamp: new Date().toISOString()
        };
        
    } catch (error) {
        debugLog('=== Design Navigation Initialization Failed ===', error);
        
        // Return error indicator
        return {
            success: false,
            error: error.message,
            timestamp: new Date().toISOString()
        };
    }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
    debugLog('DOM is still loading, waiting for DOMContentLoaded...');
    document.addEventListener('DOMContentLoaded', () => {
        debugLog('DOMContentLoaded event fired, starting initialization...');
        initializeDesignNavigation();
    });
} else {
    debugLog('DOM is already ready, starting initialization immediately...');
    initializeDesignNavigation();
}

// Export functions for manual testing (if needed)
window.DesignNavigation = {
    initialize: initializeDesignNavigation,
    verify: verifySetup,
    updateSelection: updateSelectionState,
    updateGiftCardImage: updateGiftCardImage,
    detectCurrentDesign: detectCurrentDesign,
    config: designConfig,
    urls: designUrls,
    urlMapping: urlToDesign,
    // Helper function to manually test selection
    testSelection: function(designLabel) {
        debugLog(`Manual test: Selecting ${designLabel}`);
        updateSelectionState(designLabel);
        verifySetup();
    },
    // Helper function to test URL detection
    testUrlDetection: function() {
        debugLog('Testing URL detection...');
        const detected = detectCurrentDesign();
        debugLog(`Detected design: ${detected}`);
        return detected;
    },
    // Helper function to test gift card image update
    testImageUpdate: function(designLabel) {
        debugLog(`Manual test: Updating gift card image for ${designLabel}`);
        updateGiftCardImage(designLabel);
        verifySetup();
    }
};

debugLog('Design Navigation script loaded and ready');
